/**
 * Centralized Color Palette
 *
 * All colors used throughout the application.
 * Extracted from hardcoded values to enable easy theming and future UI library migration.
 */

const colors = {
  // Primary Brand Colors
  primary: {
    main: 'rgb(62, 166, 214)', // Primary blue
    light: 'rgb(74, 194, 224)', // Lighter blue (gradient end)
    dark: 'rgb(50, 140, 180)', // Darker blue for hover states
    contrastText: '#ffffff',
  },

  // Secondary Colors
  secondary: {
    main: '#000000', // Black (used in buttons, focus states)
    light: '#333333',
    dark: '#000000',
    contrastText: '#ffffff',
  },

  // Background Colors
  background: {
    default: '#ffffff',
    paper: '#ffffff',
    beige: 'rgb(255, 247, 240)', // SignIn page background
    lightGray: '#f0f0f0', // Search bar background
  },

  // Text Colors
  text: {
    primary: '#000000',
    secondary: 'rgba(0, 0, 0, 0.6)',
    disabled: 'rgba(0, 0, 0, 0.38)',
    hint: 'rgba(0, 0, 0, 0.38)',
  },

  // Border & Divider
  divider: 'rgb(214, 214, 214)', // Card borders

  // Interaction States
  action: {
    // Pastel sky tone — the same `#f0f9ff` used as the brand pastel
    // hover across the curriculum builder, learner sidebar, and the
    // create menu. Replaces a saturated `rgb(0,153,255)` that made
    // every ListItemButton hover read as a heavy primary fill.
    hover: '#f0f9ff',
    hoverOpacity: 0.04,
    selected: 'rgba(0, 0, 0, 0.08)',
    selectedOpacity: 0.08,
    disabled: 'rgba(0, 0, 0, 0.26)',
    disabledBackground: 'rgba(0, 0, 0, 0.12)',
    disabledOpacity: 0.38,
    focus: 'rgba(0, 0, 0, 0.12)',
    focusOpacity: 0.12,
    activatedOpacity: 0.12,
  },

  // Shadows
  shadow: {
    card: '0px 5px 20px -10px rgb(107, 107, 107)',
  },

  // Success, Error, Warning, Info (standard MUI colors - can be customized)
  //
  // `softBg`/`softText` are the pale "status surface" tokens: the tinted
  // background + readable dark text used on informational chips, banners,
  // pills, and table rows (e.g. the amber CE banner, red danger rows, green
  // "published" pills). They intentionally stay semantic (not tenant-brand)
  // and are defined here so components stop hardcoding the tints. Reach them
  // in `sx` via `bgcolor: 'warning.softBg'`, `color: 'error.softText'`, etc.
  success: {
    main: '#2e7d32',
    light: '#4caf50',
    dark: '#1b5e20',
    contrastText: '#ffffff',
    softBg: '#e8f5e9',
    softText: '#1b5e20',
  },
  error: {
    main: '#d32f2f',
    light: '#ef5350',
    dark: '#c62828',
    contrastText: '#ffffff',
    softBg: '#fdecea',
    softText: '#a02525',
  },
  warning: {
    main: '#ed6c02',
    light: '#ff9800',
    dark: '#e65100',
    contrastText: '#ffffff',
    softBg: '#fff8e1',
    softText: '#7a5a00',
  },
  info: {
    main: '#0288d1',
    light: '#03a9f4',
    dark: '#01579b',
    contrastText: '#ffffff',
  },
};

export default colors;
